/**
 * Formatação de data/hora de atendimento — ponto ÚNICO da regra de "ordem de chegada".
 *
 * Alguns parceiros atendem por ordem de chegada: o atendimento tem DATA, não tem HORA.
 * O banco não tem coluna de hora separada de propósito — `scheduledAt` continua sendo um
 * DateTime único (com hora 00:00 nesses casos), o que preserva ordenação, filtros por
 * período, joins e relatórios sem alteração nenhuma. Quem sabe que a hora não vale é a
 * flag `arrivalOrder`.
 *
 * Consequência: TODO ponto que imprime o horário para uma pessoa ler (PDF, lembrete,
 * e-mail, tela) precisa passar por aqui. Formatar `scheduledAt` direto com 'HH:mm' faz o
 * sistema anunciar "às 00:00" para um paciente que deveria ler "por ordem de chegada".
 */
const TIME_ZONE = 'America/Sao_Paulo';
/** Rótulo usado quando não há hora definida. Um só lugar — evita variações no texto. */
export const ARRIVAL_ORDER_LABEL = 'por ordem de chegada';
function parse(value) {
    return value instanceof Date ? value : new Date(value);
}
/**
 * Data no fuso de São Paulo. O `scheduledAt` é gravado em UTC; formatar sem fuso joga o
 * atendimento das 21h para o dia seguinte.
 */
export function formatAppointmentDate(scheduledAt) {
    if (!scheduledAt)
        return '';
    return new Intl.DateTimeFormat('pt-BR', {
        timeZone: TIME_ZONE,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(parse(scheduledAt));
}
/**
 * Hora do atendimento — string vazia quando é por ordem de chegada OU quando não há hora
 * definida.
 *
 * Devolver vazio (e não "00:00") é proposital: quem monta a frase decide o que colocar no
 * lugar, e um `if (time)` esquecido some com a hora em vez de imprimir uma mentira.
 *
 * Meia-noite (00:00 em São Paulo) é tratada como "sem hora": o agendamento nasce com hora
 * zerada quando o operador não informa horário (mesma âncora usada pela flag
 * `arrivalOrder`). Anunciar "às 00:00" para um paciente é sempre erro — nenhuma clínica
 * atende à meia-noite. Quando a hora realmente importa ela nunca é 00:00.
 */
export function formatAppointmentTime(scheduledAt, arrivalOrder) {
    if (!scheduledAt || arrivalOrder)
        return '';
    const time = new Intl.DateTimeFormat('pt-BR', {
        timeZone: TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
    }).format(parse(scheduledAt));
    return time === '00:00' ? '' : time;
}
/**
 * Frase pronta para o paciente e para os documentos:
 *
 *   "12/03/2026 às 14:30"
 *   "12/03/2026 (por ordem de chegada)"
 *   "12/03/2026"                          (sem hora definida — meia-noite)
 *   "" quando não há data
 */
export function formatAppointmentDateTime(scheduledAt, arrivalOrder) {
    if (!scheduledAt)
        return '';
    const date = formatAppointmentDate(scheduledAt);
    if (arrivalOrder)
        return `${date} (${ARRIVAL_ORDER_LABEL})`;
    const time = formatAppointmentTime(scheduledAt, arrivalOrder);
    return time ? `${date} às ${time}` : date;
}
/**
 * Zera a hora de uma data que passa a ser "por ordem de chegada".
 *
 * A hora anterior não pode sobreviver: um exame remarcado de "14:30" para "ordem de
 * chegada" continuaria ordenando (e aparecendo na agenda) às 14:30, e qualquer código que
 * lesse a hora sem checar a flag voltaria a mentir.
 *
 * Zera em SÃO PAULO, não em UTC — zerar em UTC empurra o registro para o dia anterior às
 * 21h.
 */
export function stripTimeForArrivalOrder(scheduledAt) {
    const d = parse(scheduledAt);
    // Componentes do dia como são vistos em São Paulo.
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(d); // yyyy-mm-dd
    // Meia-noite local convertida para o instante UTC correspondente. O offset do Brasil
    // é -03:00 (sem horário de verão desde 2019).
    return new Date(`${parts}T00:00:00-03:00`);
}
//# sourceMappingURL=format.js.map